import React, { lazy } from 'react';
import IconRightly from '@/assets/images/menu/icon-rightly.react.svg?react';
import { MultiApp } from '@/config/context';
import { isPro } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { CapitalPlatform } from '@/infrastructure/utils/app';
import { MenuIcon } from '@/presentation/components/menu-icon';
import { stores } from '@/presentation/store';
import { IRouteConfig } from '../types';

const validator = (route: IRouteConfig) => {
  const config = stores.RightlyStore.routes?.find(config => config.path === route.path);
  if (!config) {
    return false;
  }
  if (!config?.products?.length) {
    return true;
  }
  return config.products.includes(getProductId());
};

export const rightlyRoutes = (): IRouteConfig[] => [
  {
    path: '/compliance',
    name: isPro() ? '应用合规' : 'Rightly合规',
    icon: <MenuIcon icon={IconRightly} />,
    sort: 86,
    versions: [MultiApp.WOA, MultiApp.Internal, MultiApp.Pro],
    platforms: [
      CapitalPlatform.Android,
      CapitalPlatform.IOS,
      CapitalPlatform.HarmonyOS,
      CapitalPlatform.Mac,
      CapitalPlatform.VisionOS,
    ],
    routes: [
      {
        path: '/compliance/homepage',
        name: '产品介绍',
        component: lazy(() => import('@/presentation/pages/compliance/homepage')),
        onMenuClick: () => reportClick(PAGE_ID.compliance.homepage, 'click_pro2rightly'),
        validator,
      },
      {
        path: '/compliance/scan',
        name: '合规测评',
        component: lazy(() => import('@/presentation/pages/compliance/scan')),
        validator,
      },
      {
        path: '/compliance/index',
        name: '指标与报告',
        flag: 'subMenu',
        validator: route => !!route.routes?.length,
        routes: [
          {
            path: '/compliance/analytics',
            name: '指标分析',
            component: lazy(() => import('@/presentation/pages/compliance/analytics')),
            validator,
          },
          {
            path: '/compliance/report',
            name: '合规报告',
            component: lazy(() => import('@/presentation/pages/compliance/report')),
            platforms: [CapitalPlatform.Android, CapitalPlatform.IOS],
            validator,
          },
          {
            path: '/compliance/agreement',
            name: '用户同意管理',
            component: lazy(() => import('@/presentation/pages/compliance/agreement')),
            platforms: [CapitalPlatform.Android, CapitalPlatform.IOS],
            validator,
          },
        ],
      },
      {
        path: '/compliance/invoke',
        name: '调用合规',
        flag: 'subMenu',
        validator: route => !!route.routes?.length,
        routes: [
          {
            path: '/compliance/issues/list',
            name: '隐私API调用',
            component: lazy(() => import('@/presentation/pages/compliance/issues/list')),
            validator,
          },
          {
            path: '/compliance/issues/detail',
            name: '隐私API调用详情',
            component: lazy(() => import('@/presentation/pages/compliance/issues/detail')),
            validator,
            hideInMenu: true,
          },
          {
            path: '/compliance/modules/list',
            name: '分场景管控',
            component: lazy(() => import('@/presentation/pages/compliance/modules/list')),
            platforms: [CapitalPlatform.Android, CapitalPlatform.IOS],
            validator,
          },
          {
            path: '/compliance/modules/detail',
            name: '分场景管控详情',
            component: lazy(() => import('@/presentation/pages/compliance/modules/detail')),
            platforms: [CapitalPlatform.Android, CapitalPlatform.IOS],
            validator,
            hideInMenu: true,
          },
        ],
      },
      {
        path: '/compliance/specific',
        name: '专项合规',
        flag: 'subMenu',
        validator: route => !!route.routes?.length,
        routes: [
          {
            path: '/compliance/self-launch/list',
            name: '自启动&关联启动',
            component: lazy(() => import('@/presentation/pages/compliance/self-launch/list')),
            platforms: [CapitalPlatform.Android],
            validator,
          },
          {
            path: '/compliance/self-launch/detail',
            name: '自启动&关联启动详情',
            component: lazy(() => import('@/presentation/pages/compliance/self-launch/detail')),
            platforms: [CapitalPlatform.Android],
            validator,
            hideInMenu: true,
          },
          {
            path: '/compliance/network/list',
            name: '网络传输敏感字段',
            component: lazy(() => import('@/presentation/pages/compliance/network/list')),
            platforms: [CapitalPlatform.Android, CapitalPlatform.IOS],
            validator,
          },
          {
            path: '/compliance/network/detail',
            name: '网络传输敏感字段详情',
            component: lazy(() => import('@/presentation/pages/compliance/network/detail')),
            platforms: [CapitalPlatform.Android, CapitalPlatform.IOS],
            validator,
            hideInMenu: true,
          },
          {
            path: '/compliance/file-access/list',
            name: '文件访问监控',
            component: lazy(() => import('@/presentation/pages/compliance/file-access/list')),
            platforms: [CapitalPlatform.Android],
            validator,
          },
          {
            path: '/compliance/file-access/detail',
            name: '文件访问监控详情',
            component: lazy(() => import('@/presentation/pages/compliance/file-access/detail')),
            platforms: [CapitalPlatform.Android],
            validator,
            hideInMenu: true,
          },
          {
            path: '/compliance/download/list',
            name: '下载六要素',
            component: lazy(() => import('@/presentation/pages/compliance/download/list')),
            platforms: [CapitalPlatform.Android],
            validator,
          },
          {
            path: '/compliance/download/detail',
            name: '下载六要素详情',
            component: lazy(() => import('@/presentation/pages/compliance/download/detail')),
            platforms: [CapitalPlatform.Android],
            validator,
            hideInMenu: true,
          },
        ],
      },
      {
        path: '/compliance/config/list',
        name: '配置列表',
        component: lazy(() => import('@/presentation/pages/compliance/config/list')),
        validator,
        hideInMenu: true,
      },
    ],
  },
];

export default rightlyRoutes;
